import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  signal,
} from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Guid, isEmptyArray, isNil, Nillable } from '@web/utils';
import { MultimediaType } from '../../../shared/enums/multimedia-type';
import { AdminMultimediaImageUpload } from '../../models/admin-multimedia-image-upload';
import { MultimediaChooseOrUploadMultipleComponent } from '../multimedia-choose-or-upload-multiple/multimedia-choose-or-upload-multiple.component';

@Component({
  selector: 'app-multimedia-choose-or-upload-multiple-control',
  imports: [MultimediaChooseOrUploadMultipleComponent],
  templateUrl: './multimedia-choose-or-upload-multiple-control.component.html',
  styleUrl: './multimedia-choose-or-upload-multiple-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(
        () => MultimediaChooseOrUploadMultipleControlComponent,
      ),
      multi: true,
    },
  ],
})
export class MultimediaChooseOrUploadMultipleControlComponent implements ControlValueAccessor {
  protected readonly disabled = signal(false);
  protected readonly multimediaIds = signal<Nillable<Guid[]>>([]);

  /* prettier-ignore */
  protected readonly allowedMimeTypes = 'image/png, image/apng, image/vnd.mozilla.apng, image/jpeg, image/gif, image/webp, image/svg+xml, image/svg';
  protected readonly maxFileSize = 5 * 1000 * 1000;
  protected readonly allowedMultimediaTypes: MultimediaType[] = [
    MultimediaType.Image,
  ];

  private onChange!: (value: Nillable<AdminMultimediaImageUpload[]>) => void;
  private onTouched!: () => void;

  public writeValue(value: Nillable<AdminMultimediaImageUpload[]>): void {
    if (isNil(value)) {
      this.multimediaIds.set(null);

      return;
    }

    this.multimediaIds.set(value.map(({ id }) => id));
  }

  public registerOnChange(
    fn: (value: Nillable<AdminMultimediaImageUpload[]>) => void,
  ): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public handleChooseClickEvent(ids: Guid[]): void {
    this.onChange(ids.map((id) => new AdminMultimediaImageUpload({ id })));
    this.onTouched();
  }

  public handleClearClickEvent(ids: Guid[]): void {
    if (isEmptyArray(ids)) {
      this.onChange(null);
    } else {
      this.onChange(ids.map((id) => new AdminMultimediaImageUpload({ id })));
    }

    this.onTouched();
  }
}
